/**
 * AffiliatedDirectoryPage — the public list of independent churches and
 * ministries under the Archbishop's covering.
 *
 * Deliberately its own directory: nothing here counts towards the FWM
 * assembly total or appears in the /churches groupings.
 */
import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowRight, Search, MapPin, UserRound, HandHeart, Info } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import FwmShell from '@/components/fwm/FwmShell';
import { FwmHero, FwmStatStrip } from '@/components/fwm/FwmHero';
import { useHero } from '@/components/fwm/useHero';
import { FWM_IMAGERY } from '@/components/fwm/imagery';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AffiliateCard = ({ a }) => (
  <Link to={`/affiliated-ministries/${a.slug}`} className="fwm-card block p-7 group"
        data-testid={`affiliate-card-${a.slug}`}>
    <span className="fwm-kicker">{a.org_type === 'ministry' ? 'Ministry' : 'Church'}</span>
    <h3 className="fwm-display text-xl mt-2 leading-snug">{a.org_name}</h3>
    {a.pastor_name && (
      <p className="mt-3 flex items-center gap-2 text-xs text-[color:var(--fwm-muted)]">
        <UserRound className="h-3.5 w-3.5" />
        {a.pastor_title ? `${a.pastor_title} ` : ''}{a.pastor_name}
      </p>
    )}
    <p className="mt-1.5 flex items-center gap-2 text-xs text-[color:var(--fwm-muted)]">
      <MapPin className="h-3.5 w-3.5" />
      {[a.city, a.country].filter(Boolean).join(', ') || '—'}
    </p>
    {a.description && (
      <p className="mt-4 text-sm leading-relaxed text-[color:var(--fwm-muted)] line-clamp-3">
        {a.description}
      </p>
    )}
    <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold text-[color:var(--fwm-accent-dark)]">
      View profile <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
    </span>
  </Link>
);

export default function AffiliatedDirectoryPage() {
  const [q, setQ] = useState('');
  const [country, setCountry] = useState('all');
  const [type, setType] = useState('all');

  const hero = useHero('affiliates', {
    image_url: FWM_IMAGERY.congregation || FWM_IMAGERY.choir,
    eyebrow: 'Under the covering',
    title: 'Affiliated churches',
    accent: '& ministries',
    blurb: 'Independent works walking with the Archbishop — distinct from the '
      + 'Faith World Ministries church network.',
  });

  const { data, isLoading } = useQuery({
    queryKey: ['affiliate-directory'],
    queryFn: () => fetch(`${API_URL}/api/affiliates/directory`).then((r) => r.json()),
    staleTime: 120000,
  });

  const all = data?.affiliates || [];
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return all.filter((a) => {
      if (country !== 'all' && a.country !== country) return false;
      if (type !== 'all' && a.org_type !== type) return false;
      if (!needle) return true;
      return [a.org_name, a.pastor_name, a.city, a.country, a.description]
        .filter(Boolean).some((v) => v.toLowerCase().includes(needle));
    });
  }, [all, q, country, type]);

  return (
    <FwmShell ctaLabel="Apply for affiliation" ctaLink="/affiliated-ministries/apply"
              testId="affiliated-directory" overDark>
      <FwmHero
        testId="affiliates-hero"
        image={hero.image_url}
        fallbackImage={hero.fallback_image_url}
        video={hero.video_url}
        mediaPriority={hero.media_priority}
        eyebrow={hero.eyebrow}
        title={hero.title}
        accent={hero.accent}
        blurb={hero.blurb}
        actions={(
          <Link to="/affiliated-ministries/apply" className="fwm-btn fwm-btn-lg"
                data-testid="affiliates-apply-cta">
            Apply for affiliation <ArrowRight className="h-4 w-4" />
          </Link>
        )}
      />

      <FwmStatStrip testId="affiliates-stats" stats={[
        [isLoading ? '—' : all.length, 'Affiliates'],
        [isLoading ? '—' : (data?.countries?.length || 0), 'Countries'],
        [isLoading ? '—' : all.filter((a) => a.org_type === 'church').length, 'Churches'],
        [isLoading ? '—' : all.filter((a) => a.org_type === 'ministry').length, 'Ministries'],
      ]} />

      <section className="mx-auto max-w-[1180px] px-6 lg:px-10 mt-20">
        <div className="fwm-card p-6 flex items-start gap-4" data-testid="affiliates-distinction-note">
          <Info className="h-5 w-5 shrink-0 text-[color:var(--fwm-accent-dark)] mt-0.5" />
          <p className="text-sm leading-relaxed text-[color:var(--fwm-muted)]">
            {data?.note || 'Affiliated churches and ministries are independent works under the '
              + 'covering of the Archbishop. They are not part of the FWM church network.'}{' '}
            <Link to="/churches" className="font-semibold text-[color:var(--fwm-accent-dark)]"
                  data-testid="affiliates-to-fwm-churches">
              Browse the FWM church network instead
            </Link>
          </p>
        </div>
      </section>

      <section className="border-y border-[color:var(--fwm-line)] bg-[color:var(--fwm-ground-2)] mt-10">
        <div className="mx-auto max-w-[1180px] px-6 lg:px-10 py-6 flex flex-wrap items-center gap-4">
          <div className="relative flex-1 min-w-0 sm:min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[color:var(--fwm-muted)]" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
                   data-testid="affiliates-search"
                   placeholder="Search a ministry, pastor or city"
                   className="pl-10 rounded-[3px] bg-[color:var(--fwm-card)] border-[color:var(--fwm-line)]" />
          </div>
          <Select value={country} onValueChange={setCountry}>
            <SelectTrigger className="w-full max-w-[220px] rounded-[3px] bg-[color:var(--fwm-card)] border-[color:var(--fwm-line)]"
                           data-testid="affiliates-country-filter">
              <SelectValue placeholder="All countries" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All countries</SelectItem>
              {(data?.countries || []).map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="w-full max-w-[180px] rounded-[3px] bg-[color:var(--fwm-card)] border-[color:var(--fwm-line)]"
                           data-testid="affiliates-type-filter">
              <SelectValue placeholder="Churches & ministries" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Churches &amp; ministries</SelectItem>
              <SelectItem value="church">Churches only</SelectItem>
              <SelectItem value="ministry">Ministries only</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </section>

      <section className="mx-auto max-w-[1180px] px-6 lg:px-10 py-16">
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-52" />)}
          </div>
        ) : !rows.length ? (
          <div className="fwm-card p-12 text-center" data-testid="affiliates-empty">
            <HandHeart className="h-8 w-8 mx-auto text-[color:var(--fwm-muted)]" />
            <h2 className="fwm-display text-2xl mt-5">
              {all.length ? 'Nothing matches that search' : 'The first affiliates are on their way'}
            </h2>
            <p className="mt-3 text-sm text-[color:var(--fwm-muted)]">
              {all.length
                ? 'Try a different name, city or country.'
                : 'Approved affiliates appear here as soon as their membership is confirmed.'}
            </p>
            <Link to="/affiliated-ministries/apply" className="fwm-btn mt-7 inline-flex"
                  data-testid="affiliates-empty-apply">
              Apply for affiliation <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        ) : (
          <>
            <div className="flex items-baseline justify-between gap-4 mb-8">
              <h2 className="fwm-display text-3xl tracking-tight" data-testid="affiliates-result-count">
                {rows.length} affiliated {rows.length === 1 ? 'work' : 'works'}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                 data-testid="affiliates-grid">
              {rows.map((a) => <AffiliateCard key={a.id} a={a} />)}
            </div>
          </>
        )}
      </section>
    </FwmShell>
  );
}
